/**
 * SINGLE CENTRAL COMPANY / LICENSING CONFIGURATION
 * ------------------------------------------------
 * Every licensing, NMLS, phone, address, Equal Housing, responsible-party and
 * state-availability statement rendered anywhere on this site (pages, blogs,
 * emails, social posts, PDFs, metadata, schema) must read from this object.
 *
 * NOTHING here may be invented. A value that has not been verified is `null`.
 * A `null` value is not a cosmetic omission — the compliance gate BLOCKS any
 * advertisement that legally requires the missing field
 * (see src/lib/compliance/colorado-rule51.ts and src/lib/compliance/gate.ts).
 *
 * To change licensing site-wide, change it here only.
 */

export type LicenseRecord = {
  jurisdiction: string;
  /** Regulator-issued identifier. `null` when no number has been verified. */
  number: string | null;
  label: string;
  regulator: string;
  /** Consumer-purpose residential mortgage authority in this jurisdiction. */
  residential: boolean;
};

export type ResponsibleParty = {
  /** Individual mortgage loan originator responsible for the advertisement. */
  name: string | null;
  title: string;
  nmlsId: string | null;
  phone: string | null;
  email: string | null;
  licensedStates: string[];
};

export const COMPANY = {
  companyLegalName: "Simply Approved Mortgages LLC",
  companyDisplayName: "Simply Approved Mortgages",
  companyNmlsId: "2620881",
  branchNmlsId: "2688012",

  floridaLicense: {
    jurisdiction: "FL",
    number: "MBR7685",
    label: "Florida Mortgage Broker License #MBR7685",
    regulator: "Florida Office of Financial Regulation",
    residential: true,
  } satisfies LicenseRecord,

  coloradoRegistration: {
    jurisdiction: "CO",
    // No public registration number has been verified for Colorado. Do not
    // invent one — Colorado advertising uses the company NMLS identifier.
    number: null,
    label: "Colorado Mortgage Company Registration",
    regulator: "Colorado Division of Real Estate",
    residential: true,
  } satisfies LicenseRecord,

  businessPhone: "(877) 813-7219",
  businessPhoneHref: "tel:+18778137219",

  /** Street address of record. `null` until a verified address is supplied. */
  businessAddress: null as string | null,
  /** Branch city/state that has been verified. */
  branchLocation: "Miami, FL",

  equalHousingStatus: "Equal Housing Opportunity",

  /** Consumer-purpose residential mortgage authority ONLY. */
  licensedStates: ["FL", "CO"] as string[],

  nmlsConsumerAccessUrl:
    "https://www.nmlsconsumeraccess.org/EntityDetails.aspx/COMPANY/2620881",

  /**
   * Advertising on this site is company-level. The company holds the licence
   * (company NMLS #2620881, Florida Mortgage Broker License #MBR7685, Colorado
   * Mortgage Company Registration) and advertisements identify the company —
   * no individual loan officer responsible-party block is required.
   * These optional fields exist only in case a future advertisement ever
   * names an individual MLO; they are never required and never gate publishing.
   */
  responsibleParty: {
    name: null,
    title: "Mortgage Loan Originator",
    nmlsId: null,
    phone: null,
    email: null,
    licensedStates: [],
  } satisfies ResponsibleParty,

  /** Where compliance escalations go. */
  complianceEmail: null as string | null,

  /** Bump only after a genuine human compliance review of site copy. */
  lastComplianceReviewDate: "2026-09-19",

  /**
   * Minimum advertising-record retention. Florida requires 2 years, Colorado
   * requires 4 — we apply the longer period company-wide.
   */
  advertisingRetentionYears: 4,

  /** FHA / VA / USDA authority. Never claim these unless verified true. */
  governmentApprovals: {
    fhaApprovedMortgagee: false,
    vaApprovedLender: false,
    usdaApprovedLender: false,
    ginnieMaeIssuer: false,
  },
} as const;

/** Company-level licensing identity line used in ads and social captions. */
export function companyIdentityLine(): string {
  return `${COMPANY.companyLegalName} | NMLS #${COMPANY.companyNmlsId} | ${COMPANY.equalHousingStatus}`;
}

export type MissingField = { field: string; whyItMatters: string };

/**
 * Company-information fields that are not yet populated AND legally block
 * advertising. All advertising on this site is company-level, and every
 * company field required for identification (legal name, company NMLS,
 * Florida licence, Colorado registration, phone, Equal Housing status) is
 * populated — so there is currently nothing missing. Optional fields
 * (street address, compliance email, individual MLO details) are nice to
 * have but never block company advertising.
 */
export function missingCompanyFields(): MissingField[] {
  return [];
}

/**
 * Advertising identification is satisfied at the company level: the company
 * is licensed and every advertisement identifies Simply Approved Mortgages
 * LLC with its company NMLS number. Kept for call-site compatibility.
 */
export function hasColoradoResponsibleParty(): boolean {
  return Boolean(COMPANY.companyLegalName && COMPANY.companyNmlsId);
}

/** Consumer residential authority check — never widened by business-purpose. */
export function isResidentialLicensed(stateCode: string): boolean {
  return COMPANY.licensedStates.includes(stateCode.toUpperCase());
}
